'use client'

import { type AllHTMLAttributes, Fragment, useContext } from 'react'

import cn from 'clsx'
import { Box } from '@/components/Box/Box'
import { Link } from '@/components/Link/Link'
import { Text } from '@/components/Text/Text'
import { ThemeContext } from '@/utils/themeContext'
import { ChevronRight } from '@doist/icons/brand'

import css from './Breadcrumbs.module.css'

export type BreadcrumbItem = {
    text: string
    href?: string
    onClick?: () => void
}

export interface BreadcrumbsProps extends AllHTMLAttributes<HTMLDivElement> {
    items?: BreadcrumbItem[]
    className?: string
    disabled?: boolean
}

export function Breadcrumbs({
    items = [],
    className,
    disabled = false,
    ...rest
}: BreadcrumbsProps) {
    const { theme = 'todoist' } = useContext(ThemeContext)
    const textSize = 'body-2xs'
    const isLink = (item: BreadcrumbItem) => item.href || item.onClick

    return (
        <Box
            tag="nav"
            className={cn(css.breadcrumbs, css[theme], { [css.disabled]: disabled }, className)}
            aria-label={rest['aria-label'] ?? 'Breadcrumb'}
            {...rest}
        >
            <ol>
                {items.map((item, idx) => {
                    // Assumes last item is the current page
                    const isLastItem = idx === items.length - 1
                    const current = isLastItem ? 'page' : undefined

                    return (
                        <Fragment key={`breadcrumbs-${idx}-${item.text}`}>
                            <li>
                                {isLink(item) ? (
                                    <Link
                                        tag={item.onClick ? 'button' : 'a'}
                                        size={textSize}
                                        aria-current={current}
                                        disabled={disabled}
                                        tabIndex={disabled ? -1 : 0}
                                        className={css.item}
                                        {...item}
                                    >
                                        {item.text}
                                    </Link>
                                ) : (
                                    <Text
                                        className={css.item}
                                        aria-current={current}
                                        size={textSize}
                                    >
                                        {item.text}
                                    </Text>
                                )}
                            </li>
                            {!isLastItem && (
                                <li className={css.colorSecondary}>
                                    <ChevronRight aria-hidden />
                                </li>
                            )}
                        </Fragment>
                    )
                })}
            </ol>
        </Box>
    )
}
